import { Box, Button, FormControl, Link, TextField, Typography, useTheme } from '@mui/material';
import ReputationPlusImage from '../../assets/images/logos/reputationplus.png';
import React, { KeyboardEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LanguageSwitch from '../../components/Header/AvatarMenu/LanguageSwitch/LanguageSwitch';
import AuthService from '../../services/AuthService';
import { useNavigate } from 'react-router-dom';

const ForgotPassword = () => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [validationError, setValidationError] = useState<boolean>(false);
  const [requestError, setRequestError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const userEmailRef = useRef<HTMLInputElement | null>(null);
  const confirmUserEmailRef = useRef<HTMLInputElement | null>(null);

  const validateAndResetPassword = async () => {
    if (userEmailRef.current && confirmUserEmailRef.current) {
      if (
        userEmailRef.current.value !== confirmUserEmailRef.current.value ||
        userEmailRef.current.value.length === 0 ||
        confirmUserEmailRef.current.value.length === 0
      ) {
        setValidationError(true);
      } else {
        const success = await AuthService.requestPasswordReset(
          userEmailRef.current.value,
          i18n.resolvedLanguage,
          setLoading
        );
        if (success) {
          setRequestError(false);
          navigate('/login/forgot-password/success', {
            state: {
              heading: t('success_heading'),
              subheading: t('forgot_password_success_subheading'),
            },
          });
        } else {
          setRequestError(true);
        }
      }
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor:
          theme.palette.mode === 'light' ? theme.palette.divider : theme.palette.background.default,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          maxWidth: '500px',
        }}
      >
        <img
          width="50%"
          src={ReputationPlusImage}
          style={{
            marginLeft: '-20px',
          }}
          alt="Reputativ Logo"
        />
        <Typography
          variant="h4"
          sx={{ fontWeight: '700', mt: theme.spacing(15), color: theme.palette.text.primary }}
        >
          {t('forgot_password')}
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{
            mt: theme.spacing(1),
            mb: theme.spacing(2),
            color: theme.palette.text.secondary,
          }}
        >
          {t('forgot_password_hint')}
        </Typography>
        <FormControl
          fullWidth={true}
          sx={{ my: theme.spacing(3) }}
        >
          <TextField
            required
            autoFocus
            label={t('email_placeholder')}
            variant="outlined"
            size="small"
            inputRef={userEmailRef}
            error={validationError}
            helperText={validationError ? t('check_email') : null}
            onChange={(e) => {
              setValidationError(false);
              if (userEmailRef.current) {
                userEmailRef.current.value = e.target.value;
              }
            }}
            sx={{
              my: theme.spacing(2),
              backgroundColor: theme.palette.background.paper,
              borderRadius: `${theme.shape.borderRadius}px`,
              '& .MuiOutlinedInput-root:hover': {
                '& > fieldset': {
                  borderColor: theme.palette.primary.main,
                },
              },
            }}
          />
          <TextField
            required
            label={t('confirm_email_placeholder')}
            variant="outlined"
            size="small"
            inputRef={confirmUserEmailRef}
            error={validationError}
            helperText={validationError ? t('check_email') : null}
            onChange={(e) => {
              setValidationError(false);
              if (confirmUserEmailRef.current) {
                confirmUserEmailRef.current.value = e.target.value;
              }
            }}
            onKeyDown={async (event: KeyboardEvent<HTMLDivElement>) => {
              if (event.key === 'Enter') {
                await validateAndResetPassword();
              }
            }}
            sx={{
              my: theme.spacing(2),
              backgroundColor: theme.palette.background.paper,
              borderRadius: `${theme.shape.borderRadius}px`,
              '& .MuiOutlinedInput-root:hover': {
                '& > fieldset': {
                  borderColor: theme.palette.primary.main,
                },
              },
            }}
          />
          <Button
            disabled={validationError || loading}
            sx={{ my: theme.spacing(2) }}
            variant="contained"
            size="medium"
            onClick={async () => await validateAndResetPassword()}
          >
            {t('reset_password')}
          </Button>
          {requestError && (
            <Typography
              variant="subtitle2"
              sx={{ color: theme.palette.error.main, textAlign: 'center' }}
            >
              {t('request_error_retry_suggestion')}
            </Typography>
          )}
        </FormControl>
        <LanguageSwitch sx={{ p: 0 }} />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Link
            href="/login"
            variant="body1"
            underline="none"
            sx={{
              color: theme.palette.primary.main,
              mt: theme.spacing(10),
            }}
          >
            {t('back')}
          </Link>
          <Typography
            variant="body2"
            sx={{ mt: theme.spacing(5), color: theme.palette.text.secondary }}
          >
            &#169; {`Reputativ GmbH ${new Date().getFullYear()}`}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default ForgotPassword;
